import { FC, useMemo } from "react"

import { useTranslation } from "react-i18next"

import { CmsPage } from "services/cms-seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { inputClass, SectionHeading, ToggleRow } from "../CmsUi"

/** Mirrors the player frontend's buildPageGraph so the preview matches what ships. */
const buildGraph = (page: CmsPage, brand: string, origin: string): object => {
  const graph: object[] = [{ "@type": "Organization", name: brand, url: origin }]

  const crumbs = page.path === "/" ? [] : page.path.split("/").filter(Boolean)
  graph.push({
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${origin}/${page.locale}` },
      ...crumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 2,
        name: crumb.replace(/-/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase()),
        ...(index === crumbs.length - 1 ? {} : { item: `${origin}/${page.locale}/${crumbs.slice(0, index + 1).join("/")}` })
      }))
    ]
  })

  if (page.type === "article") {
    graph.push({
      "@type": "Article",
      headline: page.h1 || page.title,
      author: page.author ? { "@type": "Person", name: page.author } : undefined,
      datePublished: page.published_at ?? undefined,
      dateModified: page.updated_at
    })
  }

  const faq = page.faq.filter((entry) => entry.question.trim() && entry.answer.trim())
  if (faq.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: faq.map((entry) => ({
        "@type": "Question",
        name: entry.question,
        acceptedAnswer: { "@type": "Answer", text: entry.answer }
      }))
    })
  }

  return { "@context": "https://schema.org", "@graph": graph }
}

interface SchemaPaneProps {
  page: CmsPage
  brand: string
  onChange: (patch: Partial<CmsPage>) => void
}

export const SchemaPane: FC<SchemaPaneProps> = ({ page, brand, onChange }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const generated = useMemo(
    () => JSON.stringify(buildGraph(page, brand, site.origin), null, 2),
    [page, brand, site.origin]
  )
  const overriding = page.schema_override !== null

  return (
    <div>
      <ToggleRow
        title={t("cmsSeo.schemaOverride", { defaultValue: "Override generated structured data" })}
        description={t("cmsSeo.schemaOverrideHint", { defaultValue: "Off = generated automatically from the page content" })}
        checked={overriding}
        onChange={() => onChange({ schema_override: overriding ? null : generated })}
      />

      {overriding ? (
        <>
          <SectionHeading>{t("cmsSeo.schemaCustom", { defaultValue: "Custom JSON-LD" })}</SectionHeading>
          <textarea
            className={`${inputClass} min-h-[320px] font-mono text-xs`}
            value={page.schema_override ?? ""}
            onChange={(event) => onChange({ schema_override: event.target.value })}
            spellCheck={false}
          />
          {page.schema_override && !isValidJson(page.schema_override) && (
            <p className="mt-2 text-xs text-rose-500">
              {t("cmsSeo.schemaInvalid", { defaultValue: "This is not valid JSON — it will be dropped rather than rendered." })}
            </p>
          )}
        </>
      ) : (
        <>
          <SectionHeading>{t("cmsSeo.schemaGenerated", { defaultValue: "Generated JSON-LD (read only)" })}</SectionHeading>
          <pre className="overflow-auto rounded-xl border border-black/[0.1] bg-black/[0.02] p-3.5 font-mono text-xs leading-relaxed text-emerald-700 dark:border-white/[0.12] dark:bg-[#0c0c12] dark:text-[#8ee0b4]">
            {generated}
          </pre>
          <p className="mt-2 text-[11.5px] text-gray-400 dark:text-gray-500">
            {t("cmsSeo.schemaFaqHint", {
              defaultValue: "FAQ entries come from the FAQ block on the Content tab. Add or remove them there, not here."
            })}{" "}
            <a
              href="https://search.google.com/test/rich-results"
              target="_blank"
              rel="noreferrer"
              className="text-purple-500 underline decoration-dotted underline-offset-2"
            >
              {t("cmsSeo.testRichResults", { defaultValue: "Test in Rich Results →" })}
            </a>
          </p>
        </>
      )}
    </div>
  )
}

const isValidJson = (value: string): boolean => {
  try {
    JSON.parse(value)
    return true
  } catch {
    return false
  }
}
